// Google Tag Manager / Google Analytics helper functions
// Para usar: import { trackEvent, trackPageView } from '../lib/analytics'

declare global {
  type AnalyticsEventParams = Record<string, string | number | boolean | null | undefined>

  interface Window {
    dataLayer: Array<Record<string, unknown>>
    gtag?: (command: 'event', eventName: string, eventParams?: AnalyticsEventParams) => void
  }
}

/**
 * Verifica si Google Tag Manager / Analytics está disponible
 */
export const isAnalyticsAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    (typeof window.dataLayer !== 'undefined' || typeof window.gtag === 'function')
  )
}

/**
 * Trackea un evento personalizado en Google Analytics
 *
 * @param eventName - Nombre del evento (ej: 'button_click', 'form_submit')
 * @param eventParams - Parámetros adicionales del evento (opcional)
 *
 * @example
 * trackEvent('product_view', { product_id: '123', product_name: 'Crema Facial' })
 * trackEvent('button_click', { button_name: 'add_to_cart' })
 */
export const trackEvent = (eventName: string, eventParams?: AnalyticsEventParams): void => {
  if (!isAnalyticsAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Evento:', eventName, eventParams)
    }
    return
  }

  try {
    // Usar dataLayer.push para GTM (más compatible)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventParams,
      })
    } else if (window.gtag) {
      // Fallback a gtag si está disponible
      window.gtag('event', eventName, eventParams || {})
    }
  } catch (error) {
    console.error('[Analytics] Error al trackear evento:', error)
  }
}

/**
 * Trackea una vista de página
 *
 * @param pagePath - Ruta de la página (ej: '/products', '/recommendations')
 * @param pageTitle - Título de la página (opcional)
 *
 * @example
 * trackPageView('/recommendations', 'Recomendaciones de Productos')
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (!isAnalyticsAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Page View:', pagePath, pageTitle)
    }
    return
  }

  try {
    // Usar dataLayer.push para GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: pagePath,
        page_title: pageTitle,
      })
    } else if (window.gtag) {
      // Fallback a gtag si está disponible (para Google Analytics directo)
      // Nota: Si usas GTM, este código no se ejecutará ya que dataLayer siempre estará disponible
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
      })
    }
  } catch (error) {
    console.error('[Analytics] Error al trackear página:', error)
  }
}

/**
 * Trackea una conversión o acción importante
 *
 * @param conversionName - Nombre de la conversión
 * @param value - Valor de la conversión (opcional)
 * @param currency - Moneda (por defecto: 'USD')
 *
 * @example
 * trackConversion('purchase', 99.99, 'USD')
 */
export const trackConversion = (
  conversionName: string,
  value?: number,
  currency: string = 'USD',
): void => {
  if (!isAnalyticsAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Conversión:', conversionName, value, currency)
    }
    return
  }

  try {
    const params: AnalyticsEventParams = {
      event_category: 'conversion',
      currency,
    }

    if (value !== undefined) {
      params.value = value
    }

    // Usar dataLayer.push para GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: conversionName,
        ...params,
      })
    } else if (window.gtag) {
      // Fallback a gtag si está disponible
      window.gtag('event', conversionName, params)
    }
  } catch (error) {
    console.error('[Analytics] Error al trackear conversión:', error)
  }
}
